import { useMemo, useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';

const IMAGE_EXT = /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i;

function parseLine(line, keyBase) {
  const parts = [];
  let key = 0;
  const k = () => `${keyBase}-${key++}`;

  // Process inline formatting within a line
  // Order: images, links, bold, italic, bare URLs
  const tokens = tokenize(line);

  for (const token of tokens) {
    if (token.type === 'image') {
      parts.push(
        <span key={k()} className="block my-3">
          <img src={token.src} alt={token.alt || 'Image'} className="max-w-full rounded-xl border border-slate-200 shadow-sm"
            style={{ maxHeight: '500px', objectFit: 'contain' }} loading="lazy" data-testid="forum-inline-image"
            onError={(e) => { e.target.style.display = 'none'; }} />
        </span>
      );
    } else if (token.type === 'link') {
      parts.push(
        <a key={k()} href={token.href} target="_blank" rel="noopener noreferrer"
          className="text-[#56c6df] hover:underline inline-flex items-center gap-0.5">
          {token.text}<ExternalLink className="w-3 h-3 inline" />
        </a>
      );
    } else if (token.type === 'bold') {
      parts.push(<strong key={k()} className="font-semibold">{token.text}</strong>);
    } else if (token.type === 'italic') {
      parts.push(<em key={k()}>{token.text}</em>);
    } else if (token.type === 'bareUrl') {
      if (IMAGE_EXT.test(token.href)) {
        parts.push(
          <span key={k()} className="block my-3">
            <img src={token.href} alt="Image" className="max-w-full rounded-xl border border-slate-200 shadow-sm"
              style={{ maxHeight: '500px', objectFit: 'contain' }} loading="lazy" data-testid="forum-inline-image"
              onError={(e) => { e.target.style.display = 'none'; }} />
          </span>
        );
      } else {
        parts.push(
          <a key={k()} href={token.href} target="_blank" rel="noopener noreferrer"
            className="text-[#56c6df] hover:underline inline-flex items-center gap-0.5 break-all">
            {token.href}<ExternalLink className="w-3 h-3 inline" />
          </a>
        );
      }
    } else {
      parts.push(<span key={k()}>{token.text}</span>);
    }
  }
  return parts;
}

function tokenize(text) {
  const tokens = [];
  // Combined regex for all inline patterns
  // Order matters: images first, then links, then bold, then italic, then bare URLs
  const pattern = /!\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)|\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|\*\*(.+?)\*\*|\*(.+?)\*|(https?:\/\/[^\s<)\]]+)/g;
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    // Text before this match
    if (match.index > lastIndex) {
      tokens.push({ type: 'text', text: text.substring(lastIndex, match.index) });
    }

    if (match[1] !== undefined || match[2] !== undefined) {
      // ![alt](url) image
      tokens.push({ type: 'image', alt: match[1], src: match[2] });
    } else if (match[3] !== undefined) {
      // [text](url) link
      tokens.push({ type: 'link', text: match[3], href: match[4] });
    } else if (match[5] !== undefined) {
      // **bold**
      tokens.push({ type: 'bold', text: match[5] });
    } else if (match[6] !== undefined) {
      // *italic*
      tokens.push({ type: 'italic', text: match[6] });
    } else if (match[7] !== undefined) {
      // bare URL
      tokens.push({ type: 'bareUrl', href: match[7] });
    }

    lastIndex = pattern.lastIndex;
  }

  // Remaining text
  if (lastIndex < text.length) {
    tokens.push({ type: 'text', text: text.substring(lastIndex) });
  }

  return tokens;
}

function LinkPreview({ url }) {
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    // Only show preview for non-image URLs
    if (IMAGE_EXT.test(url)) return;
    // Extract domain for display
    try {
      const domain = new URL(url).hostname.replace('www.', '');
      setMeta({ domain, url });
    } catch {
      return;
    }
  }, [url]);

  if (!meta) return null;

  return (
    <a href={meta.url} target="_blank" rel="noopener noreferrer"
      className="block my-2 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors max-w-md no-underline"
      data-testid="link-preview">
      <div className="flex items-center gap-2">
        <img src={`https://www.google.com/s2/favicons?domain=${meta.domain}&sz=32`} alt="" className="w-5 h-5 rounded" />
        <span className="text-xs text-slate-400">{meta.domain}</span>
      </div>
      <p className="text-sm text-[#56c6df] mt-1 truncate">{meta.url}</p>
    </a>
  );
}

export default function ForumContent({ text }) {
  const { lines, linkUrls } = useMemo(() => {
    if (!text) return { lines: [], linkUrls: [] };
    const allLines = text.split('\n');
    // Collect non-image URLs for previews
    const urls = [];
    const urlRe = /(?:^|\s)(https?:\/\/[^\s<)\]]+)/g;
    let m;
    while ((m = urlRe.exec(text)) !== null) {
      const u = m[1];
      if (!IMAGE_EXT.test(u) && !urls.includes(u)) urls.push(u);
    }
    // Also from [text](url) links
    const linkRe = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
    while ((m = linkRe.exec(text)) !== null) {
      const u = m[2];
      if (!IMAGE_EXT.test(u) && !urls.includes(u)) urls.push(u);
    }
    return { lines: allLines, linkUrls: urls.slice(0, 3) }; // max 3 previews
  }, [text]);

  if (!text) return null;

  return (
    <div className="forum-content">
      {/* Rendered content */}
      <div>
        {lines.map((line, i) => {
          const trimmed = line.trimStart();
          const lineKey = `l${i}-${trimmed.slice(0,12)}`;
          // Bullet point
          if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
            return (
              <div key={lineKey} className="flex items-start gap-2 ml-2 my-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2 flex-shrink-0" />
                <span>{parseLine(trimmed.substring(2), i)}</span>
              </div>
            );
          }
          // Empty line = paragraph break
          if (!trimmed) return <div key={lineKey} className="h-3" />;
          // Normal line
          return <div key={lineKey}>{parseLine(line, i)}</div>;
        })}
      </div>
      {/* Link Previews */}
      {linkUrls.length > 0 && (
        <div className="mt-3 space-y-1">
          {linkUrls.map(url => <LinkPreview key={url} url={url} />)}
        </div>
      )}
    </div>
  );
}
